/**
 * API 실패를 사람이 읽는 한 줄로.
 *
 * 🔴 두 가지가 새어 나가고 있었다(2026-09-11 점검).
 *
 * 1. **`[object Object]`** — 저장 실패 처리가 `String(json.error)` 였는데, 인증
 *    미들웨어의 401 은 `error` 가 문자열이 아니라 `{ code, message }` 객체다.
 *    액세스 토큰은 1시간짜리이고 에디터에는 갱신 로직이 없어서, 한 시간 편집한
 *    뒤 저장을 누르면 **반드시** 이 화면을 만난다. 편집 내용이 걸린 순간에
 *    「[object Object]」를 보여주고 있었다.
 * 2. 그 밖의 실패는 서버 영문 원문이 그대로 떴다(`highlight not found` 등).
 *
 * 401/403 은 «무엇을 하면 되는지»가 분명하므로 따로 안내한다 — 새로고침해도
 * 안 되고, 대시보드에서 다시 열어야 새 토큰을 받는다.
 */
export function apiErrorMessage(status: number, json: unknown, fallback: string): string {
  if (status === 401 || status === 403) {
    return '로그인이 만료됐어요. 스트리머 대시보드에서 에디터를 다시 열어 주세요.';
  }
  /*
   * 5xx 는 서버 탓이라 사용자가 할 수 있는 건 «잠시 뒤 다시»뿐이다. 그런데 «무엇이»
   * 안 됐는지는 부르는 쪽만 안다 — 저장인지, 불러오기인지, AI 대화인지. 그래서 여기서
   * 한 문장으로 뭉뚱그리지 않고 호출자가 준 문구를 쓴다(AI 대화 실패에 「불러올 수
   * 없어요」가 뜨던 것을 고친 것이다).
   */
  if (status >= 500) return fallback;

  const err = (json as { error?: unknown } | null)?.error;
  const raw =
    typeof err === 'string'
      ? err
      : err && typeof err === 'object' && 'message' in err
        ? String((err as { message?: unknown }).message ?? '')
        : '';

  /*
   * 서버 메시지는 한국어일 때도 영문일 때도 있다(`highlight not found` 등).
   * 영문 원문을 그대로 띄우면 사용자에게 아무 도움이 안 되므로, **한글이 들어 있을
   * 때만** 서버 문구를 쓰고 아니면 우리 문장으로 답한다.
   */
  if (raw.trim() && /[가-힣]/.test(raw)) return raw;
  if (status === 404) return '그 프로젝트를 찾을 수 없어요.';
  return fallback;
}

/**
 * 던져진 것을 사용자에게 보여줄 한 줄로.
 *
 * 🔴 **영문 원문을 그대로 띄우지 않는다.** 오프라인에서 저장하면 fetch 가
 * `TypeError: Failed to fetch` 를 던지는데, 그걸 그대로 토스트에 올리면
 * 사용자는 무슨 일인지도, 뭘 해야 하는지도 알 수 없다(2026-09-11 점검에서 확인).
 * 한글이 들어 있는 메시지만 우리가 만든 것으로 보고 통과시킨다.
 */
export function humanError(e: unknown, fallback: string): string {
  const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  return /[가-힣]/.test(msg) ? msg : fallback;
}
